import * as React from "react";
import { resolveSuggestionPlainText, type AnalysisParagraphResult } from "../../agreement";
import { ClampedContent } from "./ClampedContent";
import { DiffText } from "./DiffText";
import { AiMicroBtn } from "./components/ui/AiMicroBtn";
import { AiOutlineBtn } from "./components/ui/AiOutlineBtn";
import { AiSpinner } from "./components/ui/AiSpinner";
import { SuggestionEditor } from "./SuggestionEditor";
import { IconCheck, IconCopy, IconPencil, IconRefresh } from "./icons";
import { copyToClipboard } from "./utils";

export type SuggestionViewProps = {
  row: AnalysisParagraphResult;
  status: "pending" | "accepted" | "rejected";
  isRetrying: boolean;
  retryError?: string;
  busy: boolean;
  isEditing: boolean;
  draftText: string;
  displayOverride: string | null;
  onDraftChange: (text: string) => void;
  onEnterEdit: () => void;
  onApplyEdit: () => void;
  onCancelEdit: () => void;
  onRetry: () => void;
  onClearRetryError?: () => void;
  onAccept: () => void;
  onReject: () => void;
  onEditKeyDown?: (e: React.KeyboardEvent) => void;
  pulseHighlight?: boolean;
};

export const SuggestionView: React.FC<SuggestionViewProps> = ({
  row,
  status,
  isRetrying,
  retryError,
  busy,
  isEditing,
  draftText,
  displayOverride,
  onDraftChange,
  onEnterEdit,
  onApplyEdit,
  onCancelEdit,
  onRetry,
  onClearRetryError,
  onAccept,
  onReject,
  onEditKeyDown,
  pulseHighlight,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [copyError, setCopyError] = React.useState(false);
  const copiedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const canAct = !row.compliant && status === "pending" && !isRetrying;
  const canReapply = !row.compliant && status === "accepted" && !isRetrying;
  const showApplyActions = canAct || canReapply;
  const plainSuggestion = resolveSuggestionPlainText(row);
  const copyText = displayOverride ?? plainSuggestion;

  const handleCopy = async () => {
    const ok = await copyToClipboard(copyText);
    if (ok) {
      setCopyError(false);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1200);
    } else {
      setCopyError(true);
    }
  };

  if (isEditing) {
    return (
      <SuggestionEditor
        value={draftText}
        onChange={onDraftChange}
        onApply={onApplyEdit}
        onCancel={onCancelEdit}
        disabled={busy}
        onKeyDown={onEditKeyDown}
      />
    );
  }

  const showSuggestion =
    Boolean(row.wordChanges?.length) ||
    Boolean(row.suggestion.trim()) ||
    displayOverride !== null ||
    canAct;

  return (
    <div
      className={`ai-analysis__content${pulseHighlight ? " ai-analysis__content--pulse" : ""}`}
      aria-busy={isRetrying}
    >
      <div className="ai-analysis__content-toolbar">
        <div className="ai-analysis__toolbar-left">
          <AiMicroBtn
            title="Copy suggestion"
            disabled={busy || isRetrying || !copyText}
            onClick={() => void handleCopy()}
          >
            <IconCopy />
            {copied ? <span className="ai-analysis__copied-tip">Copied</span> : null}
          </AiMicroBtn>
        </div>
        <div className="ai-analysis__toolbar-right">
          <AiOutlineBtn disabled={busy || isRetrying} onClick={onRetry}>
            {isRetrying ? (
              <>
                <AiSpinner size="sm" label="Retrying" />
                Retrying…
              </>
            ) : (
              <>
                <IconRefresh size={11} />
                Retry
              </>
            )}
          </AiOutlineBtn>
          {canAct ? (
            <AiOutlineBtn disabled={busy || isRetrying} onClick={onEnterEdit}>
              <IconPencil size={11} />
              Edit
            </AiOutlineBtn>
          ) : null}
        </div>
      </div>

      <div className="ai-analysis__content-body">
        {showSuggestion ? (
          <ClampedContent>
            {displayOverride !== null ? (
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {displayOverride}
              </span>
            ) : row.wordChanges && row.wordChanges.length > 0 ? (
              <DiffText changes={row.wordChanges} dimmed={isRetrying} />
            ) : row.suggestion.trim() ? (
              <span
                className={isRetrying ? "ai-analysis__dimmed" : undefined}
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {row.suggestion}
              </span>
            ) : canAct ? (
              <span>Remove paragraph entirely</span>
            ) : null}
          </ClampedContent>
        ) : (
          <span className="ai-analysis__muted">No suggestion available.</span>
        )}

        {isRetrying ? (
          <div className="ai-analysis__overlay">
            <AiSpinner label="Generating" />
            <span>Generating a new suggestion…</span>
          </div>
        ) : null}
      </div>

      {copyError ? (
        <div className="ai-analysis__error-banner" role="alert">
          <span>Couldn&apos;t copy to clipboard.</span>
        </div>
      ) : null}

      {retryError ? (
        <div className="ai-analysis__error-banner">
          <span>Couldn&apos;t regenerate. Try again.</span>
          <button
            type="button"
            className="ai-analysis__error-link"
            onClick={() => {
              onClearRetryError?.();
              onRetry();
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {showApplyActions ? (
        <div className="ai-analysis__content-footer">
          {canAct ? (
            <AiOutlineBtn disabled={busy || isRetrying} onClick={onReject}>
              Reject
            </AiOutlineBtn>
          ) : null}
          <AiOutlineBtn primary disabled={busy || isRetrying} onClick={onAccept}>
            <IconCheck size={11} />
            {busy ? "Applying…" : canReapply ? "Apply again" : "Accept"}
          </AiOutlineBtn>
        </div>
      ) : null}
    </div>
  );
};
